import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { useBulkRejectAgentActions } from "@/accounts-payable/query";
import { DISMISS_ICON_BUTTON } from "./dismiss-icon-class";

/** Shown in the dialog, and announced with its title when it opens. */
const WARNING =
  "Nothing is sent to ServiceTitan for them. Actions you already decided stay as they are. This can't be undone.";

/**
 * The header's ✕: dismisses every action of the card's run still awaiting
 * review. It sits in the slot above the rows' ✕ column, so it reads as the
 * same control for the whole invoice. Rejection is final, so it confirms.
 */
export const DismissAllButton = ({
  businessId,
  actionIds,
  documentName,
  hiddenCount = 0,
}: {
  businessId: string | null;
  actionIds: string[];
  /** "Johnstone Supply JS-0099421", naming the invoice in the dialog. */
  documentName: string;
  hiddenCount?: number;
}) => {
  // The ids the dialog was opened on, null while closed. The queue refetches
  // while it is open, and a newer run would swap in proposals the reviewer
  // never saw, so confirming rejects this snapshot rather than the props.
  const [confirmingIds, setConfirmingIds] = useState<string[] | null>(null);
  const bulkReject = useBulkRejectAgentActions(businessId);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const count = (confirmingIds ?? actionIds).length;
  const hiddenNote =
    hiddenCount === 1
      ? "The 1 action the filter hides stays pending."
      : `The ${hiddenCount} actions the filter hides stay pending.`;
  const message = hiddenCount > 0 ? `${WARNING} ${hiddenNote}` : WARNING;

  const confirm = () => {
    if (!confirmingIds) return;
    bulkReject.mutate(confirmingIds, {
      onSuccess: () => setConfirmingIds(null),
      onError: () =>
        toast.error("Couldn't dismiss the actions. Please try again."),
    });
  };

  return (
    <Dialog
      open={confirmingIds !== null}
      onOpenChange={(next) =>
        !bulkReject.isPending && setConfirmingIds(next ? actionIds : null)
      }
    >
      {/* A DialogTrigger, so closing the dialog hands focus back here. */}
      <Tooltip>
        <TooltipTrigger
          render={
            <DialogTrigger
              render={
                <Button
                  variant="ghost"
                  color="danger"
                  size="icon"
                  aria-label={`Dismiss all for ${documentName}`}
                  className={DISMISS_ICON_BUTTON}
                />
              }
            />
          }
        >
          <XIcon aria-hidden />
        </TooltipTrigger>
        <TooltipContent>Dismiss all</TooltipContent>
      </Tooltip>
      <DialogContent initialFocus={cancelRef}>
        {/* Clear of the close ✕ on both sides, so a long invoice name
            wraps before reaching it and the title stays centred. */}
        <DialogHeader className="pr-10">
          <DialogTitle>
            {hiddenCount > 0
              ? `Dismiss the ${count} shown actions for ${documentName}?`
              : `Dismiss ${count} remaining actions for ${documentName}?`}
          </DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="secondary"
            disabled={bulkReject.isPending}
            onClick={() => setConfirmingIds(null)}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            disabled={bulkReject.isPending}
            aria-busy={bulkReject.isPending || undefined}
            onClick={confirm}
          >
            {bulkReject.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Dismiss {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
